import React, { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import TermsPopup from './TermsPopup';

interface TermsGuardProps {
  children: ReactNode;
}

/**
 * 관문을 적용하지 않는 경로.
 * 정책(마켓홈)과 순환 방지(로그인·상점약관·QR 역매핑)를 위한 예외다.
 * ProviderRouter.tsx 의 실제 path 와 1:1 대응하므로, 라우트를 추가/변경하면 여기도 함께 본다.
 *
 * ★RequiredNoticeGuard.tsx 의 동명 함수와 같은 목록이다 — 두 관문이 같은 라우트 그룹을
 *   감싸므로 면제 목록이 어긋나면 한쪽만 로그인을 막는 사고가 난다. 라우트 변경 시 두 파일을
 *   함께 고칠 것.
 */
function isExemptPath(pathname: string): boolean {
  // 마켓홈 — md 확정 면제(비로그인도 보는 곳, 관문 안 띄움).
  if (pathname === '/' || pathname === '') return true;

  // 로그인 계열 — 관문이 걸리면 로그인 자체가 불가능해진다.
  //  ProviderRouter: '/login'(:312) '/login/:storeNo'(:389)
  //                  '/login/oauth2/code/kakao'(:365) '/login/oauth2/code/naver'(:373)
  if (pathname === '/login' || pathname.startsWith('/login/')) return true;
  // ★'/auth/google/callback'(:381)은 경로에 login 문자열이 없다 — 접두사 필터로 못 잡히므로 별도 처리.
  if (pathname.startsWith('/auth/')) return true;

  // QR 역매핑/랜딩 — 여기서 막으면 목적지 판정 전에 팝업이 떠 원래 목적지가 유실된다.
  //  역매핑 후 목적지 화면에서 관문이 잡으므로 우회 경로가 되지 않는다.
  //  ProviderRouter: '/app/qr/:type/:uuid'(:230) '/app/link/:type/:uuid'(:239)
  if (pathname.startsWith('/app/')) return true;

  // 상점 약관 동의 — 관문이 걸리면 ConsentGuard.tsx:52 가 보낸 목적지에 도달할 수 없어
  //  상점 약관 동의가 불가능해진다. ProviderRouter: '/:storeNo/consent'(:397)
  if (/^\/[^/]+\/consent\/?$/.test(pathname)) return true;

  // 마켓 약관·방침 전문 — 이 관문이 띄우는 팝업의 [보기]가 여는 문서다(TermsPopup.tsx:28-29).
  //  면제하지 않으면 미동의 상태에서 전문 대신 같은 팝업이 다시 뜬다
  //  (TermsPopup 은 phase==='consent' 에서 children 을 렌더하지 않는다).
  //  ProviderRouter: '/naegift-app-terms' '/naegift-app-privacy'
  if (
    pathname === '/naegift-app-terms' ||
    pathname === '/naegift-app-privacy'
  ) {
    return true;
  }

  return false;
}

/**
 * 미동의 약관 관문.
 * ProviderRouter.tsx 의 path:'/' 그룹 element 에서 공지 관문 바깥을 감싼다(약관 → 공지 순).
 *
 * 조회·팝업·동의 저장·통과 판정은 TermsPopup 이 담당한다.
 * 비로그인은 /profile 이 401 이라 그대로 통과한다(TermsPopup 이 fail-open 처리).
 *
 * ★여기서 import 하는 './TermsPopup' 의 default export 는 TermsPopupBoundary 다.
 *   렌더 예외 시 children 을 통과시켜(fail-open) 페이지가 백지가 되지 않는다.
 */
export default function TermsGuard({
  children,
}: TermsGuardProps): React.JSX.Element {
  const { pathname } = useLocation();
  if (isExemptPath(pathname)) {
    return <>{children}</>;
  }
  return <TermsPopup>{children}</TermsPopup>;
}
